import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const wrap = { maxWidth: 820, margin: '0 auto', padding: '40px 20px 60px', color: 'var(--text)' };
const h1 = { fontSize: 28, fontWeight: 800, marginBottom: 4 };
const h2 = { fontSize: 17, fontWeight: 700, margin: '26px 0 8px' };
const p = { fontSize: 14.5, lineHeight: 1.7, color: 'var(--muted)', margin: '0 0 12px' };
const meta = { fontSize: 13, color: 'var(--muted)', marginBottom: 20 };

export default function TermsPage() {
  return (
    <div style={wrap}>
      <Helmet>
        <title>Terms &amp; Conditions — Algovex</title>
        <meta name="description" content="Algovex Terms and Conditions." />
      </Helmet>
      <h1 style={h1}>Terms &amp; Conditions</h1>
      <div style={meta}>Last updated: May 22, 2026</div>

      <p style={p}>
        These Terms &amp; Conditions (“Terms”) govern your access to and use of the Algovex website,
        applications, and services (collectively, the “Service”) operated by Algovex LLC (“Algovex,”
        “we,” “us,” or “our”). By accessing or using the Service, you agree to be bound by these
        Terms. If you do not agree, do not use the Service.
      </p>

      <h2 style={h2}>1. Not financial advice</h2>
      <p style={p}>
        Algovex is a research and educational tool for building and testing trading strategies. The
        Service does not provide financial, investment, tax, or legal advice, and nothing on the
        Service constitutes a recommendation to buy, sell, or hold any security or to pursue any
        trading strategy. Backtested, simulated, and paper-traded results are hypothetical, may not
        reflect real market conditions, and do not guarantee future results. You are solely
        responsible for your own trading and investment decisions and any resulting outcomes.
      </p>

      <h2 style={h2}>2. Eligibility &amp; accounts</h2>
      <p style={p}>
        You must be at least 18 years old and able to form a binding contract to use the Service.
        You are responsible for maintaining the confidentiality of your account credentials and for
        all activity that occurs under your account. Notify us promptly of any unauthorized use.
      </p>

      <h2 style={h2}>3. Subscriptions &amp; billing</h2>
      <p style={p}>
        Certain features require a paid subscription. Paid plans are billed in advance on a recurring
        basis through our third-party payment processor (Stripe). Subscriptions renew automatically
        until cancelled. You may cancel at any time; cancellation takes effect at the end of the
        current billing period, and fees already paid are non-refundable except where required by
        law. We may change plan pricing or features on prospective notice.
      </p>

      <h2 style={h2}>4. Acceptable use</h2>
      <p style={p}>
        You agree not to misuse the Service, including by attempting to disrupt or reverse-engineer
        it, accessing it through automated means outside of provided interfaces, infringing others’
        rights, or using it for any unlawful purpose. We may suspend or terminate accounts that
        violate these Terms.
      </p>

      <h2 style={h2}>5. Market data &amp; third parties</h2>
      <p style={p}>
        The Service relies on data and services from third parties (including market data providers
        and AI providers). We do not guarantee the accuracy, completeness, or timeliness of any data,
        and such data may be delayed or contain errors. Your use of third-party services may also be
        subject to their own terms.
      </p>

      <h2 style={h2}>6. Intellectual property</h2>
      <p style={p}>
        The Service, including its software, design, and content, is owned by Algovex LLC and
        protected by intellectual-property laws. You retain ownership of the strategies and content
        you create; you grant us a limited license to host and process them to operate the Service.
      </p>

      <h2 style={h2}>7. Disclaimers</h2>
      <p style={p}>
        THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE” WITHOUT WARRANTIES OF ANY KIND, WHETHER
        EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
        AND NON-INFRINGEMENT. We do not warrant that the Service will be uninterrupted, error-free, or
        secure.
      </p>

      <h2 style={h2}>8. Limitation of liability</h2>
      <p style={p}>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, ALGOVEX LLC AND ITS AFFILIATES WILL NOT BE LIABLE FOR
        ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY TRADING OR
        INVESTMENT LOSSES, ARISING OUT OF OR RELATING TO YOUR USE OF THE SERVICE. Our total liability
        for any claim relating to the Service will not exceed the amounts you paid to us in the twelve
        months preceding the claim.
      </p>

      <h2 style={h2}>9. Termination</h2>
      <p style={p}>
        You may stop using the Service at any time. We may suspend or terminate your access if you
        breach these Terms or if we discontinue the Service. Provisions that by their nature should
        survive termination will survive.
      </p>

      <h2 style={h2}>10. Changes to these Terms</h2>
      <p style={p}>
        We may update these Terms from time to time. Material changes will be reflected by updating
        the “Last updated” date above and, where appropriate, by additional notice. Your continued
        use of the Service after changes take effect constitutes acceptance of the revised Terms.
      </p>

      <h2 style={h2}>11. Governing law</h2>
      <p style={p}>
        These Terms are governed by the laws of the United States and the state in which Algovex LLC
        is organized, without regard to conflict-of-laws principles.
      </p>

      <h2 style={h2}>12. Contact</h2>
      <p style={p}>
        Questions about these Terms? Reach us via our <Link to="/contact" style={{ color: 'var(--teal)' }}>Contact page</Link>.
      </p>
    </div>
  );
}
